import { GetPdpIntegratedData_pdp_size_info_item_list } from '@domains/pdp-common';

import { ComparisonResult } from './similarity-analyzer';
import { SizeInfoItem } from './types';

/**
 * 서버에서 내려오는 사이즈 데이터를 UI 컴포넌트(Table)에서 사용하기 쉬운 형태로 변환합니다.
 *
 * 요구사항:
 * - 서버 데이터는 Key-Value List 형태(세로형)로 제공됨.
 * - UI 테이블은 가로형(Header-Row) 구조이므로 Transposing(행/열 변환) 필요.
 * - 데이터가 없는 셀은 '-'로 일괄 처리하여 UI 일관성 유지.
 *
 * @param list 서버로부터 받은 원본 사이즈 정보 리스트
 * @returns UI 렌더링에 최적화된 SizeInfoItem 배열 (headers, rows 구조)
 */
export const transformSizeData = (list: GetPdpIntegratedData_pdp_size_info_item_list[]): SizeInfoItem[] => {
  return list.map((item) => {
    const rawValueList = item.value_list || [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sizeCategoryId = (item as any).size_category_id;

    if (rawValueList.length === 0) {
      return {
        imageUrl: item.image_url || '',
        headers: [],
        rows: [],
        description: item.description || [],
        sizeCategoryId,
      };
    }

    /**
     * 원본 데이터 구조:
     * [
     *   ["사이즈", "총기장", "가슴단면"], // 헤더
     *   ["FREE", "66", "57"],        // 1행
     *   ["S", "64", "55"]            // 2행
     * ]
     *
     * 목표 데이터 구조 (가로형 테이블):
     * headers: ["사이즈", "총기장", "가슴단면"]
     * rows: [["FREE", "66", "57"], ["S", "64", "55"]]
     */
    const [headers, ...rows] = rawValueList;

    // 데이터가 null인 경우 '-'로 처리
    const processedRows = rows.map((row) => row.map((cell) => cell || '-'));

    return {
      imageUrl: item.image_url || '',
      headers,
      rows: processedRows,
      description: item.description || [],
      isSizeComparable: item.is_size_comparable,
      sizeCategoryId,
    };
  });
};

/**
 * 테이블 레이아웃 계산을 위해 데이터를 평탄화(Flatten)합니다.
 *
 * 목적:
 * - 테이블의 컬럼 너비를 계산하기 위해서는 헤더뿐만 아니라 실제 데이터의 길이도 알아야 합니다.
 * - 헤더와 첫 번째 행의 데이터를 합쳐서 1차원 배열로 만듭니다.
 *
 * @param rawItem 변환된 사이즈 정보 아이템
 * @returns 헤더와 첫 번째 행의 데이터가 합쳐진 문자열 배열
 */
export const getFlattenedData = (rawItem: SizeInfoItem | undefined): string[] => {
  if (!rawItem) {
    return [];
  }

  const headers = rawItem.headers || [];
  const rows = rawItem.rows || [];

  // 모든 헤더와 모든 행의 데이터를 하나의 배열로 합침
  const allData = [...headers, ...rows.flat()];
  return allData;
};

/**
 * 원본 사이즈 정보에 비교 분석 결과(유사도, 하이라이팅)와 비교 상품 데이터를 병합하지 않고,
 * 순수 상품 데이터에 하이라이팅 정보와 추천 라벨링만 적용합니다.
 * (비교 행은 별도의 MySizeRow 컴포넌트로 분리되어 처리됨)
 *
 * 요구사항:
 * 1. 하이라이팅 개수가 가장 많은 사이즈 행에 "추천" 라벨 추가 (단일 옵션인 경우 제외)
 * 2. 동률인 경우 모든 해당 사이즈에 "추천" 라벨 추가
 * 3. 유사도 분석 결과(highlightFlags)를 원본 데이터에 주입하여 UI에서 파란색 텍스트 처리
 * 4. compareRow(내 사이즈)는 rows 배열에 병합하지 않음 (중복 렌더링 방지)
 *
 * @param rawItem 변환된 원본 사이즈 정보
 * @param similarityResult 사이즈 유사도 분석 결과 (하이라이팅 플래그, 가장 유사한 인덱스 등)
 * @param compareRow 비교 대상 상품의 데이터 행 (참조용, 실제 병합은 안함)
 * @returns UI에 최종적으로 렌더링될 SizeInfoItem (하이라이팅 정보 포함)
 */
export const applySimilarityToItem = (
  rawItem: SizeInfoItem | undefined,
  similarityResult: ComparisonResult | null,
  compareRow: string[] | undefined,
): SizeInfoItem | undefined => {
  if (!rawItem) {
    return undefined;
  }

  const baseRows = rawItem.rows;
  const initialRows = [...baseRows];

  // mostSimilarIndices가 있으면 사용, 없으면 mostSimilarIndex로 폴백
  const targetIndices = similarityResult?.mostSimilarIndices ?? 
    (similarityResult?.mostSimilarIndex !== undefined && similarityResult.mostSimilarIndex !== -1 
      ? [similarityResult.mostSimilarIndex] 
      : []);

  const hasSimilarity = targetIndices.length > 0;

  if (!hasSimilarity) {
    return {
      ...rawItem,
      rows: initialRows,
      highlightFlags: similarityResult?.highlightFlags,
      hasCompareRow: !!compareRow,
    };
  }

  // 상품의 row가 하나(단일 옵션)일 경우 "추천" 워딩 제거
  const isSingleOption = baseRows.length === 1;

  const updatedRows = initialRows.map((row, index) => {
    // 추천 대상이 아닌 행은 그대로 반환
    if (!targetIndices.includes(index)) return row;

    // 타겟 행인 경우 라벨 수정
    const [originalLabel, ...restCells] = row;
    const newLabel = isSingleOption ? originalLabel : `${originalLabel} 추천`;

    return [newLabel, ...restCells];
  });

  return {
    ...rawItem,
    rows: updatedRows,
    highlightFlags: similarityResult?.highlightFlags,
    hasCompareRow: !!compareRow,
  };
};
